import { FC } from 'react';

import { avatarDefaultUrl } from '../../constants/images';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import useFormattedDateTimeZone from '../../hooks/useFormattedDateTimeZone';
import { ResaleData } from '../../types/resale';
import { User } from '../../types/user';
import { formatEventDate } from '../../utils/formatEventDate';
import * as Styles from './styles';

type ResaleTicketCardProps = {
  resaleData: ResaleData;
  user?: User | null;
  showBuyerInfo?: boolean;
  overridePrice?: number;
};

const ResaleTicketCard: FC<ResaleTicketCardProps> = ({
  resaleData,
  user,
  showBuyerInfo = false,
  overridePrice,
}) => {
  const { translate } = useI18nContext();
  const { formatDate } = useFormattedDateTimeZone();

  const eventDateFrom = resaleData.event.dates?.from || resaleData.event.from;
  const eventTimezone = resaleData.event.dates?.timezone || resaleData.event.timezone || 'America/Sao_Paulo';
  const formattedDate = eventDateFrom
    ? formatEventDate(formatDate(eventDateFrom, eventTimezone))
    : null;

  // overridePrice pode ser > saleValue (spread FastPass) — não usar usePriceDisplay aqui
  const displayPrice = overridePrice ?? resaleData.saleValue;
  const avatarUrl = user?.pictures?.[0]?.link || avatarDefaultUrl;

  return (
    <Styles.Wrapper>
      {formattedDate && (
        <Styles.DateBadge>
          <Styles.DateWeekday>{formattedDate.weekdayShort}</Styles.DateWeekday>
          <Styles.DateDivision />
          <Styles.DateDay>{formattedDate.day}</Styles.DateDay>
          <Styles.DateMonth>{formattedDate.month}</Styles.DateMonth>
          <Styles.DateDivision />
          <Styles.DateTime>{formattedDate.time}</Styles.DateTime>
        </Styles.DateBadge>
      )}

      <Styles.Container>
        <Styles.TicketContent>
          <Styles.TicketHeader>
            <Styles.TicketInfo>
              <Styles.TicketName>{resaleData.ticket.guestType.name}</Styles.TicketName>
              <Styles.TicketPrice>
                {new Intl.NumberFormat('pt-BR', {
                  style : 'currency',
                  currency : 'BRL'
                }).format(displayPrice)}
              </Styles.TicketPrice>
            </Styles.TicketInfo>
          </Styles.TicketHeader>

          {showBuyerInfo && user && (
            <Styles.BuyerInfo>
              <Styles.Avatar src={avatarUrl} alt={user.name} />
              <Styles.BuyerDetails>
                <Styles.BuyerName>
                  {user.name} <Styles.YouBadge>({translate(['INFORMATION', 'YOU'])})</Styles.YouBadge>
                </Styles.BuyerName>
                <Styles.BuyerEmail>{user.email}</Styles.BuyerEmail>
              </Styles.BuyerDetails>
            </Styles.BuyerInfo>
          )}
        </Styles.TicketContent>
      </Styles.Container>
    </Styles.Wrapper>
  );
};

export default ResaleTicketCard;
